import { KEYCODE } from '@/constants';
import { debounce } from '@/utils/debounce';

/**
 * Class definition for `wp-tabs` element
 *
 * @class WPTabs
 * @extends {HTMLElement}
 */
class WPTabs extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._selectedTab = null;
    this._tabpanelsContainer = null;
    this._tabsContainer = null;
    this._slider = null;
    this._id = this.id
      ? this.id
      : 'tabs-' + Math.random().toString(36).substr(2, 5);
    this._handleResize = debounce(() => {
      if (this._selectedTab && this._slider) {
        this._moveSlider(this._selectedTab);
      }
    }, 50);
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-tabs';
  }

  /**
   * Getter for `scrollable` attribute
   */
  get scrollable() {
    return this.hasAttribute('scrollable');
  }

  /**
   * Setter for `scrollable` attribute
   */
  set scrollable(value) {
    if (Boolean(value)) {
      this.setAttribute('scrollable', '');
    } else {
      this.removeAttribute('scrollable');
    }
  }

  /**
   * Getter for `slider` attribute
   */
  get slider() {
    return this.hasAttribute('slider');
  }

  /**
   * Setter for `slider` attribute
   */
  set slider(value) {
    if (Boolean(value)) {
      this.setAttribute('slider', '');
    } else {
      this.removeAttribute('slider');
    }
  }

  /**
   * Getter for `heading` attribute
   */
  get heading() {
    return this.hasAttribute('heading');
  }

  /**
   * Setter for `heading` attribute
   */
  set heading(value) {
    if (Boolean(value)) {
      this.setAttribute('heading', '');
    } else {
      this.removeAttribute('heading');
    }
  }

  /**
   * Getter for `prefix` attribute
   */
  get prefix() {
    return this.hasAttribute('prefix') ? this.getAttribute('prefix') : 'c-tabs';
  }

  /**
   * Setter for `prefix` attribute
   */
  set prefix(value) {
    if (String(value.length)) {
      this.setAttribute('prefix', value);
    } else {
      this.removeAttribute('prefix');
    }
  }

  /**
   * Returns array of tabs
   *
   * @returns {HTMLElement[]} Array of tabs
   * @private
   * @readonly
   */
  get _tabs() {
    return [...this.querySelectorAll('wp-tab')];
  }

  /**
   * Returns array of tabpanels
   *
   * @returns {HTMLElement[]} Array of tabpanels
   * @private
   * @readonly
   */
  get _tabpanels() {
    return [...this.querySelectorAll('wp-tabpanel')];
  }

  /**
   * Returns the current index of selected tab within tabs array
   *
   * @returns {Number} Current index of selected tab
   * @readonly
   */
  get _index() {
    let currentIndex = -1;

    this._tabs.forEach((tab, index) => {
      if (tab === this._selectedTab) {
        currentIndex = index;
      }
    });

    return currentIndex;
  }

  /**
   * Check if the tabs container has a scrollbar
   *
   * @returns {Boolean} Tabs container is overflowing
   * @readonly
   */
  get _isOverflowing() {
    return this._tabsContainer.scrollWidth > this._tabsContainer.offsetWidth;
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    if (this._tabs.length !== this._tabpanels.length) {
      console.warn('Missing tabs or tabpanels');
      return;
    }

    // When child elements are defined, setup tabs
    Promise.all([
      customElements.whenDefined('wp-tab'),
      customElements.whenDefined('wp-tabpanel'),
    ]).then(() => {
      this._createTabpanelsContainer();
      this._createTabsContainer();
      this.classList.add(this.prefix);

      // Check hash
      this._checkHash();

      const selectedTab = this._tabs.find((tab) => tab.selected);

      setTimeout(() => {
        // If no tab selected, open first tab
        if (!selectedTab) {
          this._tabs[0].selected = true;
          this._gotoTab(this._tabs[0]);
        } else {
          // Open selected tab
          this._gotoTab(selectedTab);
        }

        // Add event listeners
        this.addEventListener('tabchanged', this._handleTabChanged, false);
        this.addEventListener('keydown', this._handleKeyDown, false);
      }, 100);
    });

    // If slider, add `resize` event listener to fix slider ratio on resize
    if (this.slider) {
      this._handleResize = this._handleResize.bind(this);
      window.addEventListener('resize', this._handleResize, false);
    }
  }

  /**
   * Lifecycle event when element is detached from DOM
   */
  disconnectedCallback() {
    if (this.slider) {
      window.removeEventListener('resize', this._handleResize, false);
    }
  }

  /**
   * Create container for tabs to create tablist
   *
   * @private
   */
  _createTabsContainer() {
    const container = document.createElement('div');
    const tablist = document.createElement('div');

    container.className = `${this.prefix}__tabs-container screen`;
    tablist.className = `${this.prefix}__tablist`;
    tablist.setAttribute('role', 'tablist');

    if (this.scrollable) {
      tablist.classList.add(`${this.prefix}__tablist--scrollable`);
    }

    // Append each tab to container
    this._tabs.forEach((tab, index) => {
      tab.id = `${this._id}-tab-${index}`;
      tab.setAttribute('aria-controls', `${this._id}-tabpanel-${index}`);
      tab.classList.add(`${this.prefix}__tab`);

      if (this.scrollable) {
        tab.classList.add(`${this.prefix}__tab--grow`);
      }

      tablist.appendChild(tab);
    });

    // Add slider
    if (this.slider) {
      this._slider = document.createElement('div');

      this._slider.classList.add(`${this.prefix}__slider`);
      tablist.appendChild(this._slider);
    }

    container.appendChild(tablist);
    this.insertBefore(container, this.firstChild);
    this._tabsContainer = container;
    this._tabList = tablist;
  }

  /**
   * Create container for tabpanels to animate height nicely
   *
   * @private
   */
  _createTabpanelsContainer() {
    const div = document.createElement('div');

    div.className = `${this.prefix}__tabpanels`;

    // Append each tabpanel to container
    this._tabpanels.forEach((tabpanel, index) => {
      tabpanel.id = `${this._id}-tabpanel-${index}`;
      tabpanel.setAttribute('aria-labelledby', `${this._id}-tab-${index}`);
      tabpanel.classList.add(`${this.prefix}__tabpanel`);

      // If has `src` attribute, lazyload
      if (tabpanel.hasAttribute('src')) {
        tabpanel.classList.add(`${this.prefix}__tabpanel--lazy`);
      }

      // Create heading
      if (this.heading) {
        const heading = document.createElement('div');
        heading.textContent = this._tabs[index].textContent;
        heading.setAttribute('role', 'heading');
        heading.tabIndex = 0;
        tabpanel.insertBefore(heading, tabpanel.firstChild);
      }

      div.appendChild(tabpanel);
    });

    this.insertBefore(div, this.firstChild);
    this._tabpanelsContainer = div;
  }

  /**
   * Event handler for `tabchanged` event
   *
   * @private
   * @param {UIEvent} evt
   */
  _handleTabChanged(evt) {
    this._gotoTab(evt.detail);
  }

  /**
   * Go to selected tab
   *
   * @private
   * @param {HTMLElement} tab Selected tab
   */
  _gotoTab(tab) {
    const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));

    // If already opem, return
    if (tab === this._selectedTab) {
      return;
    }

    // Show new tabpanel
    tabpanel.collapsed = false;

    // Unselect current selected tab
    if (this._selectedTab) {
      const selectedTabpanel = document.getElementById(
        this._selectedTab.getAttribute('aria-controls'),
      );

      // Transition tabpanels container height
      this._transitionHeight(
        selectedTabpanel.clientHeight,
        tabpanel.clientHeight,
      );

      // Deselect previous selected tab
      this._selectedTab.selected = false;
      selectedTabpanel.collapsed = true;
    } else {
      // Do not animate on first render
      tabpanel.animating = false;
    }

    // Update selected tab
    this._selectedTab = tab;

    // Scroll tab into view
    this._scrollIntoView();

    // Move slider
    if (this.slider) {
      this._moveSlider();
    }
  }

  /**
   * Event handler for `keydown` event
   *
   * @private
   * @param {UIEvent} evt
   */
  _handleKeyDown(evt) {
    const keyCode = evt.which;
    const nextTab = this._tabs[this._index + 1];
    const firstTab = this._tabs[0];
    const previousTab = this._tabs[this._index - 1];
    const lastTab = this._tabs[this._tabs.length - 1];

    // If focus is not a tab, return
    if (!this._tabs.includes(evt.target)) {
      return;
    }

    switch (keyCode) {
      // Left/Up
      case KEYCODE.LEFT:
      case KEYCODE.UP:
        evt.preventDefault();
        evt.stopPropagation();

        if (!previousTab) {
          lastTab.focus();
        } else {
          previousTab.focus();
        }

        break;

      // Right/Down
      case KEYCODE.RIGHT:
      case KEYCODE.DOWN:
        evt.preventDefault();
        evt.stopPropagation();

        if (!nextTab) {
          firstTab.focus();
        } else {
          nextTab.focus();
        }

        break;

      // Home
      case KEYCODE.HOME:
        evt.preventDefault();
        evt.stopPropagation();

        firstTab.focus();

        break;

      // End
      case KEYCODE.END:
        evt.preventDefault();
        evt.stopPropagation();

        lastTab.focus();

        break;

      // Enter/Space
      case KEYCODE.SPACE:
      case KEYCODE.ENTER:
        break;
      default:
        break;
    }
  }

  /**
   * Animate the Tabpanels container height when changing tabs
   *
   * @private
   * @param {Number} from From height in px
   * @param {Number} to To height in px
   */
  _transitionHeight(from, to) {
    this._tabpanelsContainer.style.height = `${from}px`;
    this._tabpanelsContainer.$once(
      'transitionend',
      (evt) => {
        if (evt.target.tagName.toLowerCase() === 'wp-tabpanel') {
          this._tabpanelsContainer.style.height = null;
        }
      },
      false,
    );

    // Set slight delay so element can transition from one height to the other
    setTimeout(() => {
      this._tabpanelsContainer.style.height = `${to}px`;
    });
  }

  /**
   * Scroll selected tab into view
   *
   * @private
   */
  _scrollIntoView() {
    // If tablist not overflowing, return
    if (!this._isOverflowing) {
      return;
    }

    const totalWidth = this._tabsContainer.scrollLeft + this.clientWidth;
    const { clientWidth, offsetLeft } = this._selectedTab;
    const itemOffset = clientWidth + offsetLeft;
    let additionalOffset = clientWidth * 0.1;

    // Don't add an offset if selecting the last tab
    if (this._index === this._tabs.length - 1) {
      additionalOffset = 0;
    }

    if (offsetLeft < this._tabsContainer.scrollLeft) {
      this._sideScroll(
        'left',
        Math.max(this._tabsContainer.scrollLeft + additionalOffset, 0),
      );
    } else if (totalWidth < itemOffset) {
      this._sideScroll('right', -(totalWidth - itemOffset - additionalOffset));
    }
  }

  /**
   * Animate horizontal scroll
   *
   * @private
   * @param {string} direction Which way to horizontall scroll
   * @param {Number} distance Distance in px to scroll
   */
  _sideScroll(direction, distance) {
    const STEP = 10;
    let scrollAmount = 0;
    var slideTimer = setInterval(() => {
      if (direction === 'right') {
        this._tabsContainer.scrollLeft += STEP;
      } else {
        this._tabsContainer.scrollLeft -= STEP;
      }

      scrollAmount += STEP;

      if (scrollAmount >= distance) {
        window.clearInterval(slideTimer);
      }
    }, 20);
  }

  /**
   * Move slider to selected tab
   *
   * @private
   */
  _moveSlider() {
    const r = this._tabList.getBoundingClientRect();
    const w = this._tabList.scrollWidth;
    const tabRect = this._selectedTab.getBoundingClientRect();
    const tabOffsetLeft = tabRect.left - r.left;
    const position = {
      width: this._calcPercent(tabRect.width, w),
      left: this._calcPercent(tabOffsetLeft, w),
    };

    setTimeout(() => {
      this._slider.style.transform = `translateX(${
        position.left
      }%) scaleX(${position.width / 100})`;
    }, 50);
  }

  /**
   * Helper function to get percentage of tab width in relation to container
   *
   * @private
   * @param {Number} tabWidth Width of selected tab
   * @param {Number} tabsContainerWidth  Width of tabs container
   * @returns {Number} Percentage of width tab is in relation to container
   */
  _calcPercent(tabWidth, tabsContainerWidth) {
    return (100 * tabWidth) / tabsContainerWidth;
  }

  _checkHash() {
    // Check if hash exists in URL
    const hash = window.location.hash;
    let tab;

    // If no hash, return
    if (!hash) {
      return;
    }

    tab = document.querySelector(hash);

    // If no tabpanel exists, return
    if (!tab) {
      return;
    }

    // if already open, return
    if (tab.selected) {
      return;
    }

    tab.selected = true;
  }
}

customElements.define(WPTabs.is, WPTabs);
